import type {
  ShopFooterDesign,
  ShopHeaderDesign,
  ShopMenuItem,
  ShopSectionLayoutType,
  ShopSectionType,
} from "../../../../domain/types/company-shop";

/**
 * 프리셋은 MES 프론트 상수다. 백엔드는 프리셋 개념을 모르고 개별 필드만 저장한다.
 * 프리셋을 고르면 아래 값이 개별 필드로 "복사"되고, 이후 사용자가 어떤 필드든
 * 손대면 presetKey 를 CUSTOM 으로 내린다. 프리셋 정의가 바뀌어도 이미 저장된
 * 쇼핑몰은 영향받지 않는다.
 */
export const CUSTOM_PRESET_KEY = "CUSTOM";

export type HeaderPresetKey = "DEFAULT" | "DARK" | "MINIMAL";
export type FooterPresetKey = "DEFAULT" | "DARK" | "COMPACT";

type HeaderPresetValues = Pick<
  ShopHeaderDesign,
  "backgroundColor" | "textColor" | "logoAlignment" | "stickyEnabled"
>;

type FooterPresetValues = Pick<
  ShopFooterDesign,
  | "backgroundColor"
  | "textColor"
  | "showBusinessInfo"
  | "showBusinessHours"
  | "showSns"
>;

/**
 * stickyEnabled 는 전 프리셋이 false 다. 현재 쇼핑몰 header 는 position: relative 라
 * sticky 가 아니며, 기본값을 true 로 두면 기존 헤더 동작이 바뀐다.
 * sticky 는 관리자가 직접 켜는 옵션이다.
 *
 * DEFAULT 는 "현재 쇼핑몰 그대로"를 뜻하므로 값이 실제 common.css 와 일치해야 한다.
 * DARK / MINIMAL 은 관리자가 명시적으로 고르는 값이라 별개다.
 */
export const HEADER_PRESETS: Record<HeaderPresetKey, HeaderPresetValues> = {
  DEFAULT: {
    // header { background: #fff }
    backgroundColor: "#FFFFFF",
    // header .menu-container .main-menu ... { color: #252525 }
    textColor: "#252525",
    logoAlignment: "LEFT",
    stickyEnabled: false,
  },
  DARK: {
    backgroundColor: "#111111",
    textColor: "#FFFFFF",
    logoAlignment: "LEFT",
    stickyEnabled: false,
  },
  MINIMAL: {
    backgroundColor: "#FFFFFF",
    textColor: "#111111",
    logoAlignment: "CENTER",
    stickyEnabled: false,
  },
};

export const FOOTER_PRESETS: Record<FooterPresetKey, FooterPresetValues> = {
  DEFAULT: {
    // footer { background: #f2f2f2 }
    backgroundColor: "#F2F2F2",
    // 푸터 내 color: 선언 6곳이 전부 #252525
    textColor: "#252525",
    showBusinessInfo: true,
    showBusinessHours: true,
    showSns: true,
  },
  DARK: {
    backgroundColor: "#222222",
    textColor: "#CCCCCC",
    showBusinessInfo: true,
    showBusinessHours: true,
    showSns: true,
  },
  COMPACT: {
    backgroundColor: "#FFFFFF",
    textColor: "#888888",
    showBusinessInfo: true,
    showBusinessHours: false,
    showSns: false,
  },
};

export const HEADER_PRESET_LABELS: Record<HeaderPresetKey, string> = {
  DEFAULT: "기본",
  DARK: "다크",
  MINIMAL: "미니멀",
};

export const FOOTER_PRESET_LABELS: Record<FooterPresetKey, string> = {
  DEFAULT: "기본",
  DARK: "다크",
  COMPACT: "컴팩트",
};

/**
 * 쇼핑몰 header.ejs 의 메뉴와 1:1 대응한다.
 *
 * ABOUT 은 헤더/푸터 모두 "회사 소개"(띄어쓰기)다. 예전에는 헤더가 띄어쓰기,
 * 푸터가 붙여쓰기로 갈려 있었고 "실제 화면이 그러니 맞추지 말라"는 주석이
 * 달려 있었지만, 같은 화면 안에서 표기가 갈리는 것 자체가 결함이라
 * 띄어쓰기 쪽으로 통일했다. shop 의 header.ejs / footer.ejs 와
 * 백엔드 백필 JSON 도 같은 문구여야 한다.
 */
export const DEFAULT_HEADER_MENU_ITEMS: ShopMenuItem[] = [
  { key: "ALL_PRODUCTS", label: "전체상품", visible: true, displayOrder: 0 },
  { key: "CONSULT", label: "1:1 실시간 문의", visible: true, displayOrder: 1 },
  { key: "FAQ", label: "자주 묻는 질문", visible: true, displayOrder: 2 },
  { key: "NOTICE", label: "공지사항", visible: true, displayOrder: 3 },
  { key: "PORTFOLIO", label: "포트폴리오", visible: true, displayOrder: 4 },
  { key: "ABOUT", label: "회사 소개", visible: true, displayOrder: 5 },
  { key: "MY_PAGE", label: "마이페이지", visible: true, displayOrder: 6 },
  // 2026-10-02 고객 요청으로 추가. 다른 쇼핑몰이 바뀌지 않도록 감춘 채로 두고, 쓰는 회사가 켜고 링크를 고른다.
  // 백엔드 ShopMenuItems.DEFAULT_HEADER / V59 와 같은 값이다.
  // 기본 메뉴 이름은 회사가 바꿀 수 없다(2026-10-26). 백엔드 기본 이름과 글자까지 같아야 한다
  { key: "BOOK_ORDER", label: "책자주문", visible: false, displayOrder: 7 },
  { key: "SHEET_ORDER", label: "낱장주문", visible: false, displayOrder: 8 },
  { key: "QUICK_QUOTE", label: "빠른 견적", visible: false, displayOrder: 9, href: "/shop/quote-inquiry" },
  { key: "EDIT_DESIGN", label: "편집디자인", visible: false, displayOrder: 10 },
];

/**
 * 링크(이동할 카테고리·상품·주소)를 회사가 고를 수 있는 헤더 메뉴.
 * 나머지 메뉴는 쇼핑몰이 정해 둔 페이지로만 간다.
 * MY_PAGE 는 2026-10-26 대한DH 요청(상단 메뉴 원클릭 이동)으로 추가했다.
 */
export const LINKABLE_HEADER_MENU_KEYS = ["BOOK_ORDER", "SHEET_ORDER", "QUICK_QUOTE", "EDIT_DESIGN", "MY_PAGE"];

/** 링크를 비워 두었을 때 쇼핑몰이 쓰는 기본 링크 (shop header.ejs 와 같은 값) */
export const DEFAULT_MENU_HREF_LABELS: Record<string, string> = {
  BOOK_ORDER: "전체 상품",
  SHEET_ORDER: "전체 상품",
  QUICK_QUOTE: "견적 문의",
  EDIT_DESIGN: "전체 상품",
  MY_PAGE: "내 정보 수정",
};

/** footer.ejs 하단 메뉴와 1:1 대응한다. 문구 기준은 위 헤더 주석과 같다 */
export const DEFAULT_FOOTER_MENU_ITEMS: ShopMenuItem[] = [
  { key: "ABOUT", label: "회사 소개", visible: true, displayOrder: 0 },
  { key: "NOTICE", label: "공지사항", visible: true, displayOrder: 1 },
  { key: "TERMS", label: "이용약관", visible: true, displayOrder: 2 },
  {
    // 개인정보 보호법 제30조의 법정 용어는 "개인정보처리방침"이다.
    key: "PRIVACY",
    label: "개인정보처리방침",
    visible: true,
    displayOrder: 3,
  },
];

export const DEFAULT_HEADER_DESIGN: ShopHeaderDesign = {
  presetKey: "DEFAULT",
  ...HEADER_PRESETS.DEFAULT,
  menuItems: DEFAULT_HEADER_MENU_ITEMS,
};

export const DEFAULT_FOOTER_DESIGN: ShopFooterDesign = {
  presetKey: "DEFAULT",
  ...FOOTER_PRESETS.DEFAULT,
  menuItems: DEFAULT_FOOTER_MENU_ITEMS,
};

export const SECTION_TYPE_LABELS: Record<ShopSectionType, string> = {
  BANNER: "배너",
  STRIP_BANNER: "띠배너",
  RECENTLY_VIEWED: "최근 둘러본 상품",
  BEST_SELLER: "베스트셀러",
  ALL_PRODUCTS: "전체 상품",
  PORTFOLIO: "포트폴리오",
  PRODUCT_LIST: "상품 리스트",
};

/**
 * PRODUCT_LIST 만 추가/삭제가 가능하다. 나머지는 shop 당 1개로 고정이고
 * 순서 변경과 on/off 만 할 수 있다.
 *
 * PORTFOLIO 도 고정 섹션이다. 예전에는 "쇼핑몰 메인에 포트폴리오 블록이
 * 존재한 적이 없다"는 이유로 여기서 빠져 있었지만, F-8 로 메인 섹션이
 * 생기면서 그 전제가 뒤집혔다. 메인 노출은 shop_sections.is_enabled 가
 * 정하고, 헤더 메뉴 노출은 여전히 portfolioEnabled 가 따로 정한다.
 */
export const FIXED_SECTION_TYPES: ShopSectionType[] = [
  "BANNER",
  "STRIP_BANNER",
  "RECENTLY_VIEWED",
  "BEST_SELLER",
  "ALL_PRODUCTS",
  "PORTFOLIO",
];

/**
 * 백엔드 미배포 구간에서 GET /shop/section 이 실패했을 때 합성할 기본 섹션 순서.
 * 백엔드 seed(= 현재 index.ejs 렌더링 순서 + 그 뒤에 붙는 포트폴리오)와
 * 동일한 6개여야 한다. PORTFOLIO 는 ALL_PRODUCTS 다음인 displayOrder 5 다.
 */
export const FALLBACK_SECTION_ORDER: ShopSectionType[] = [
  "BANNER",
  "STRIP_BANNER",
  "RECENTLY_VIEWED",
  "BEST_SELLER",
  "ALL_PRODUCTS",
  "PORTFOLIO",
];

/**
 * 표시 형식(GRID/LIST)을 노출하는 섹션 타입.
 *
 * 지금은 PORTFOLIO 뿐이다. layout_type 은 shop_sections 공통 컬럼이지만,
 * 쇼핑몰이 실제로 LIST 를 그리는 곳은 포트폴리오 메인 블록 하나다
 * (상품 섹션들의 CSS 에는 가로형 행 레이아웃이 아예 없다).
 * 지원하지 않는 섹션에 노출하면 관리자가 바꿔도 안 바뀌는 먹통 컨트롤이 된다
 * - COLUMN_COUNT_SECTION_TYPES 와 같은 판단 기준이다.
 */
export const LAYOUT_TYPE_SECTION_TYPES: ShopSectionType[] = ["PORTFOLIO"];

export const SECTION_LAYOUT_TYPE_LABELS: Record<
  ShopSectionLayoutType,
  string
> = {
  GRID: "그리드",
  LIST: "리스트",
};

/**
 * 표시 형식이 LIST 면 열 수가 의미가 없다.
 *
 * 쇼핑몰이 LIST 에서 `cols-N` 을 아예 붙이지 않고
 * `.portfolio-card-grid--list { grid-template-columns: 1fr }` 로 한 줄에
 * 하나씩 그린다(index.css:512). 그 상태에서 열 수 입력을 열어두면
 * 바꿔도 아무 일이 없는 먹통 컨트롤이 된다.
 */
export const isColumnCountMeaningful = (
  layoutType: ShopSectionLayoutType,
): boolean => layoutType !== "LIST";

/**
 * 열 수를 노출하는 섹션은 ALL_PRODUCTS / PRODUCT_LIST / PORTFOLIO 셋이다.
 *
 * 쇼핑몰 index.css 를 확인한 결과 셋의 레이아웃이 서로 다르다:
 * - `.all-products .products-track` : grid, repeat(4, 1fr) × 2행 (모바일 2열)
 *   → 열 수가 실제로 의미를 갖는 유일한 고정 섹션. 다만 index.js 의
 *     getItemsPerPage() 가 데스크톱 8 을 하드코딩하고 있어서, shop 이
 *     data-column-count 를 읽도록 고쳐야 비로소 안전하다 (shop 담당 F-4).
 * - `.recently-products .products-list` : grid, repeat(auto-fill, minmax(260px,1fr))
 *   → 열 수가 가용 너비로 결정된다. 고정 열 수를 받아도 쓸 데가 없다.
 * - `.best-products .products-list` : flex + wrap + justify-content:center
 *   → 애초에 그리드가 아니다.
 *
 * - `.portfolio-card-grid` : grid, `cols-2` / `cols-3` / `cols-4` 클래스로
 *   `repeat(N, 1fr)` 을 갈아끼운다 (index.css:494~510, 반응형 분기도 cols 별로
 *   따로 있다). `views/index.ejs` 가 `'portfolio-card-grid cols-' +
 *   sectionColumnCount(section)` 으로 실제 이 값을 쓴다.
 *   ※ 포트폴리오 **목록 페이지**(`portfolio/index.css` 의 `.portfolio-grid`)는
 *     `repeat(3, 1fr)` 고정이지만 그건 다른 화면이다. 메인 섹션과 혼동하지 마라.
 *
 * 그래서 RECENTLY_VIEWED / BEST_SELLER 에는 열 수를 노출하지 않는다.
 * 노출하면 관리자가 바꿔도 쇼핑몰이 안 바뀌는 먹통 컨트롤이 된다
 * (F-3 의 designProductEnabled 와 똑같은 종류의 결함).
 */
export const SECTION_COLUMN_COUNT_OPTIONS = [2, 3, 4] as const;

/** 열 수를 노출하는 섹션 타입 */
export const COLUMN_COUNT_SECTION_TYPES: ShopSectionType[] = [
  "ALL_PRODUCTS",
  "PRODUCT_LIST",
  "PORTFOLIO",
];

/**
 * Tailwind 는 클래스명을 문자열 그대로 스캔하므로 `md:grid-cols-${n}` 처럼
 * 조립하면 빌드에서 잘려나간다. 정적 문자열로 미리 나열해 둔다.
 *
 * 모바일 2열은 쇼핑몰의 @media(max-width:768px) 규칙과 같다.
 */
export const SECTION_COLUMN_CLASS: Record<number, string> = {
  2: "grid-cols-2 md:grid-cols-2",
  3: "grid-cols-2 md:grid-cols-3",
  4: "grid-cols-2 md:grid-cols-4",
};

/** 쇼핑몰 데스크톱 한 페이지 = 열 수 × 2행 (index.css 의 grid-template-rows) */
export const SECTION_GRID_ROWS = 2;

/**
 * 미리보기에서 쓰는 색. 기준은 쇼핑몰의 실제 CSS 값이다
 * (public/stylesheets/common.css, index.css — 읽기만 하고 고치지 않았다).
 *
 * 미리보기가 실제 쇼핑몰과 다른 색을 쓰면 관리자가 보고 고른 결과와 배포된
 * 화면이 달라진다. 하드코딩을 흩뿌리지 않고 여기 한 곳에 모아 둔다.
 * 헤더/푸터 색은 관리자가 직접 고르는 값이라 headerDesign / footerDesign 에서
 * 오고, 그 기본값은 HEADER_PRESETS / FOOTER_PRESETS 가 갖는다.
 */
export const PREVIEW_COLORS = {
  /** .all-products .section-header h2, .best-products .section-header .title */
  sectionTitle: "#0A0A0A",
  /** .best-products .section-header .desc */
  sectionSubtitle: "#6A7282",
  /** .all-products .product-img-wrap { background-color: #f5f5f5 } */
  thumbnailPlaceholder: "#F5F5F5",

  /** .recently-products .products-list .product-{name,price} */
  recentlyViewed: {
    productName: "#0A0A0A",
    productPrice: "#787A7D",
  },

  /** .best-products .products-list .product-item .product-{name,desc,price} */
  bestSeller: {
    productName: "#0A0A0A",
    productDesc: "#6A7282",
    productPrice: "#101828",
  },

  /** .all-products .products-track .product-{name,desc,price} — 셋 다 #252525 */
  allProducts: {
    productName: "#252525",
    productPrice: "#252525",
  },

  /**
   * PRODUCT_LIST 는 이번에 새로 생긴 섹션이라 쇼핑몰에 대응 CSS 가 아직 없다.
   * 가장 가까운 상품 그리드인 ALL_PRODUCTS 값을 따라간다. shop 의 F-5(상품 카드
   * 마크업 통일)가 끝나면 그 통일된 카드 색으로 다시 맞춰야 한다.
   */
  productList: {
    productName: "#252525",
    productPrice: "#252525",
  },

  /**
   * 포트폴리오 카드. 쇼핑몰 portfolio/index.css 의 :root 토큰 실제 값이다.
   * 상품 카드와 토큰 체계가 달라서(--text/--muted/--panel) 따로 적는다.
   */
  portfolio: {
    /** .portfolio-card { background: var(--panel) } */
    cardBackground: "#FFFFFF",
    /** .portfolio-card { border: 1px solid var(--border-light) } */
    cardBorder: "#F0F1F4",
    /** .portfolio-name { color: var(--text) } */
    title: "#111827",
    /** .portfolio-desc / .portfolio-date { color: var(--muted) } */
    summary: "#6B7280",
    /** .portfolio-client { color: var(--muted); background: var(--accent-light) } */
    clientBackground: "#F3F4F6",
    /** .portfolio-image-wrap 의 linear-gradient 시작/끝 */
    thumbnailFrom: "#F8F9FC",
    thumbnailTo: "#EDF0F7",
  },

  /** 설정 패널과 공유하는 MES 자체 UI 색 (쇼핑몰 대응값 없음) */
  mutedText: "#AEAEAE",
} as const;

